import './CreatePlaylistModal.scss';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import ImageCheckbox from '../imageCheckbox/ImageCheckbox';
import ErrorMessage from '../errorMessage/ErrorMessage';
import Modal from '../modal/Modal';
import Button from '../button/Button';
import MultiSelectDropdown from '../multiSelectDropdown/MultiSelectDropdown';
import InputField from '../inputField/InputField';
import { RESPONSE, ROUTES, MAX_DELERSHIP_GRUOPS_TO_CREATE_PLAYLIST } from '../../js/_constants';
import Server from '../../js/config/_server';
import { apiPost } from '../../js/api';
import { addManyItemsToPlaylist } from '../../api/playlists';
import { isNotEmpty } from '../../js/fpUtils';
import { userStore } from '../../store';
import { type DistributedMedium, type ModuleType } from '../../api/types';
import { type DropdownOptionType } from '../multiSelectDropdown/MultiSelectDropdown';
import { useModuleTypesStore } from '../../store';

type CreatePlaylistModalProps = {
    onCheckboxClick?: () => void;
    onClose: () => void;
    isOpen: boolean;
    isFromSelection?: boolean;
    primaryButtonText?: string;
    selectedMediaItems?: DistributedMedium[];
    onSuccess: (arg: { state: string; payload: string }) => void;
};

const CreatePlaylistModal = ({
    onCheckboxClick,
    onClose,
    isOpen,
    isFromSelection,
    primaryButtonText,
    selectedMediaItems,
    onSuccess,
}: CreatePlaylistModalProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { dealershipGroups } = userStore();

    const { isModuleTypesLoading, moduleTypes } = useModuleTypesStore();

    const [isCreating, setIsCreating] = useState(false);

    const [selectedModuleTypes, setSelectedModuleTypes] = useState<
        ModuleType['@id'][]
    >([]);

    const [selectedGroups, setSelectedGroups] = useState<any[]>([]);

    const [playlistTitle, setPlaylistTitle] = useState('');

    const [error, setError] = useState('');

    const moduleTypeDropdownOptions = useMemo(
        () =>
            moduleTypes.map(moduleType => ({
                value: moduleType[RESPONSE.ID],
                label: moduleType.title
            })),
        [moduleTypes]
    );

    const groupsOptions = useMemo(
        () =>
            dealershipGroups.map(group => ({
                value: group[RESPONSE.ID],
                label: group.title
            })),
        [dealershipGroups]
    );

    const handleTitleChange = (value: string) => setPlaylistTitle(value);

    const handleModuleTypeSelection = (selectedValues: DropdownOptionType[]) =>
        setSelectedModuleTypes(selectedValues.map(({ value }) => value));

    const handleGroupsSelection = (selectedValues: DropdownOptionType[]) =>
        setSelectedGroups(selectedValues.map(({ value }) => value));

    const handleCreatePlaylist = async (
        event: React.SyntheticEvent<HTMLButtonElement>
    ) => {
        setIsCreating(true);
        try {
            const url = Server.baseUrl + Server.services.playlists.create;
            const options = {
                body: {
                    title: playlistTitle,
                    moduleTypes: selectedModuleTypes,
                    dealershipGroups: selectedGroups
                }
            };
            const createdPlaylist = await apiPost(url, options);

            if (isFromSelection && isNotEmpty(selectedMediaItems)) {
                await addManyItemsToPlaylist(
                    createdPlaylist[RESPONSE.ID],
                    // @ts-ignore
                    selectedMediaItems
                );
            }

            if ((event.target as HTMLButtonElement).name === 'create-and-navigate-button') {
                onSuccess({
                    state: 'redirecting',
                    payload: createdPlaylist[RESPONSE.ID]
                });
                const id = createdPlaylist[RESPONSE.ID].split('/');
                return navigate(`${ROUTES.PLAYLISTS}/${id[6]}`);
            }

            return onSuccess({
                state: 'done',
                payload: createdPlaylist[RESPONSE.ID]
            });
        } catch (err) {
            return setError(
                'There was an error while creating playlist. Please try again!'
            );
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <Modal
            className="b-create-playlist-modal"
            show={isOpen}
            onClose={onClose}
        >
            <h4>{t('new_playlist_modal_title')}</h4>
            <div className="modal-content">
                {isFromSelection && (
                    <ImageCheckbox
                        key="selected_from_playlist"
                        label={t('new_playlist_from_selection')}
                        check={isFromSelection}
                        onCheck={onCheckboxClick}
                    />
                )}
                <InputField
                    isFocusDisabled
                    label={t('new_playlist_title_field')}
                    name="playlist-name"
                    onValueChangeHandler={handleTitleChange}
                    value={playlistTitle}
                />
                <MultiSelectDropdown
                    className="playlist-module-dropdown"
                    isFilterable
                    label={t('playlist_module')}
                    name="playlist-module"
                    onChange={handleModuleTypeSelection}
                    options={moduleTypeDropdownOptions}
                    isLoading={isModuleTypesLoading}
                    hint={t('create_playlist_module_type_hint')}
                    hintVisibility="focused"
                />
                <MultiSelectDropdown
                    options={groupsOptions}
                    label={t('dealership_groups_dropdown')}
                    name="dealership-groups-dropdown"
                    onChange={handleGroupsSelection}
                    defaultValue={selectedGroups}
                    isFilterable
                />
                {groupsOptions.length > MAX_DELERSHIP_GRUOPS_TO_CREATE_PLAYLIST && <p className="hint">{t('dealership_groups_hint')}</p>}
            </div>
            <div className="modal-footer">
                <Button
                    name="modal-primary-button"
                    text={primaryButtonText || t('save')}
                    clickHandler={handleCreatePlaylist}
                    secondaryButton
                    isLoading={isCreating}
                    disabled={playlistTitle.length === 0 || selectedGroups.length > MAX_DELERSHIP_GRUOPS_TO_CREATE_PLAYLIST}
                />
                <Button
                    name="create-and-navigate-button"
                    text={t('new_playlist_redirect_to')}
                    clickHandler={handleCreatePlaylist}
                    isLoading={isCreating}
                    disabled={playlistTitle.length === 0 || selectedGroups.length > MAX_DELERSHIP_GRUOPS_TO_CREATE_PLAYLIST}
                />
            </div>
            {error && <ErrorMessage message={error} />}
        </Modal>
    );
};

export default CreatePlaylistModal;